import { useMemo, useState } from 'react';
import { useData } from '../lib/data';
import { useSelection } from '../lib/selection';
import { supabase } from '../lib/supabase';
import { READONLY } from '../lib/readonly';
import { useToast } from '../lib/toast';
import { updateTaskWithRollback } from '../lib/taskWrites';
import type { Task } from '../lib/database.types';
import { DueChip } from '../components/DueChip';
import { AddTaskForm } from '../components/AddTaskForm';
import { EditableName } from '../components/EditableName';

const ACTIVE = new Set(['pending', 'in_progress', 'blocked']);

export function ClientDashboard({ slug }: { slug: string }) {
  const { clients, projects, tasks, tasksByProject, upsertClientLocal } = useData();
  const { select } = useSelection();

  const client = clients.find((c) => c.slug === slug);

  const clientProjects = useMemo(
    () => projects.filter((p) => p.client_slug === slug),
    [projects, slug],
  );

  const allProjectSlugs = new Set(clientProjects.map((p) => p.slug));
  const activeTasks = useMemo(
    () =>
      tasks.filter(
        (t) =>
          t.parent_task_id === null && allProjectSlugs.has(t.project_slug) && ACTIVE.has(t.status),
      ),
    [tasks, allProjectSlugs],
  );

  if (!client) {
    return (
      <div className="flex-1 grid place-items-center text-dim font-mono text-sm">
        client not found: {slug}
      </div>
    );
  }

  const commercialRate = (client.commercial?.rate as string | undefined) ?? '';
  const commercialType = (client.commercial?.engagement_type as string | undefined) ?? '';
  const commercial = [commercialType, commercialRate].filter(Boolean).join(' · ');
  const contextSummary = (client.context?.summary as string | undefined) ?? '';

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-5xl p-8 space-y-8">
        <header className="space-y-2">
          <div className="text-xs font-mono text-dim uppercase tracking-widest">Company</div>
          <h1 className="text-3xl font-semibold tracking-tight">
            <EditableName
              value={client.display_name}
              onSave={async (next) => {
                const { error } = await supabase
                  .from('tb_clients')
                  .update({ display_name: next })
                  .eq('slug', client.slug);
                if (error) throw new Error(error.message);
                upsertClientLocal({ ...client, display_name: next });
              }}
              inputClassName="text-3xl font-semibold tracking-tight"
            />
          </h1>
          <div className="flex items-center gap-3 text-sm text-muted font-mono">
            {client.primary_contact && <span>{client.primary_contact}</span>}
            {client.primary_role && (
              <>
                <span className="text-dim">·</span>
                <span>{client.primary_role}</span>
              </>
            )}
            <span className="text-dim">·</span>
            <span className={`chip chip-${statusToChip(client.status)}`}>{client.status}</span>
          </div>
        </header>

        {(contextSummary || commercial) && (
          <div className="panel p-5 space-y-3">
            {contextSummary && <p className="text-sm text-ink">{contextSummary}</p>}
            {commercial && (
              <div className="text-xs font-mono text-muted">
                <span className="text-dim">commercial: </span>
                {commercial}
              </div>
            )}
          </div>
        )}

        <section className="space-y-3">
          <div className="flex items-baseline justify-between">
            <h2 className="text-lg font-semibold">Builds</h2>
            <span className="text-xs text-dim font-mono">
              {clientProjects.length} · {activeTasks.length} active tasks
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {clientProjects.map((p) => {
              const pTasks = tasksByProject.get(p.slug) ?? [];
              const pActive = pTasks.filter((t) => ACTIVE.has(t.status));
              return (
                <button
                  key={p.slug}
                  onClick={() => select({ type: 'project', slug: p.slug })}
                  className="panel p-4 text-left hover:border-amber/60 hover:bg-panelHi
                             transition-colors space-y-2"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-semibold text-sm truncate">{p.name}</span>
                    <span className="text-xs text-dim font-mono tabular-nums shrink-0">
                      {pActive.length}
                    </span>
                  </div>
                  {p.description && (
                    <div className="text-[11px] text-muted font-mono line-clamp-2">
                      {p.description}
                    </div>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    {p.engagement_stage && (
                      <span className="chip chip-low">{p.engagement_stage}</span>
                    )}
                    <span className="chip chip-low">{p.state}</span>
                  </div>
                </button>
              );
            })}
            {clientProjects.length === 0 && (
              <div className="panel p-10 text-center text-dim font-mono text-sm col-span-2">
                no builds yet
              </div>
            )}
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-sm font-mono text-dim uppercase tracking-widest">Quick add</h2>
          <AddTaskForm clientSlug={slug} />
        </section>

        <section className="space-y-3">
          <div className="flex items-baseline justify-between">
            <h2 className="text-lg font-semibold">What we're doing</h2>
            <span className="text-xs text-dim font-mono">{activeTasks.length} active</span>
          </div>
          <div className="space-y-1.5">
            {activeTasks.slice(0, 12).map((t) => (
              <ActiveTaskRow key={t.id} task={t} onOpen={() => select({ type: 'task', id: t.id })} />
            ))}
            {activeTasks.length === 0 && (
              <div className="panel p-10 text-center text-dim font-mono text-sm">
                no active tasks
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function ActiveTaskRow({ task, onOpen }: { task: Task; onOpen: () => void }) {
  const { patchTaskLocal } = useData();
  const { push } = useToast();
  const [busy, setBusy] = useState(false);

  const complete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (READONLY) return;
    setBusy(true);
    const now = new Date().toISOString();
    await updateTaskWithRollback({
      task,
      patch: { status: 'completed', completed_at: now },
      patchTaskLocal,
      push,
      persist: async () => {
        const { data, error } = await supabase
          .from('tb_tasks')
          .update({ status: 'completed', completed_at: now })
          .eq('id', task.id)
          .select()
          .single();
        return { data: (data as Task | null) ?? null, error };
      },
      successToast: {
        kind: 'success',
        title: 'Task completed',
        body: task.title.slice(0, 60),
        ttlMs: 2500,
      },
      errorTitle: 'Failed to complete task',
    });
    setBusy(false);
  };

  return (
    <div
      className="panel p-3 flex items-center gap-3 hover:border-borderHi hover:bg-panelHi/40
                 transition-colors"
    >
      <button
        onClick={complete}
        disabled={busy}
        aria-label="mark complete"
        className="check-hit"
        title="mark complete"
      >
        <span className="check-dot" />
      </button>
      <button onClick={onOpen} className="flex-1 min-w-0 text-left cursor-pointer">
        <div className="text-sm truncate">{task.title}</div>
        <div className="flex items-center gap-2 text-xs text-muted font-mono mt-1 flex-wrap">
          <span className={`chip chip-${task.priority}`}>{task.priority}</span>
          <DueChip dueAt={task.due_at} />
          {task.status === 'blocked' && <span className="chip chip-critical">blocked</span>}
          {task.status === 'in_progress' && (
            <span className="chip chip-high">in progress</span>
          )}
        </div>
      </button>
    </div>
  );
}

function statusToChip(s: string): string {
  if (s === 'active') return 'medium';
  if (s === 'paused') return 'low';
  if (s === 'shipped') return 'medium';
  return 'low';
}
